/* eslint-disable @typescript-eslint/no-use-before-define,no-case-declarations */
import EventEmitter from 'eventemitter3';
import { action, observable } from 'mobx';
import { nanoid } from 'nanoid';

import type {
  AcknowledgeP2pMessage,
  BaseServerDisconnectReasonMessage,
  BaseWebsocketClientToServerPeerMessage,
  BaseWebsocketClientToServerPongMessage,
  BaseWebsocketServerErrorMessage,
  BaseWebsocketServerToClientMessage,
  BaseWebsocketServerToClientPeersConnectedMessage,
  SyncOrAcknowledgeP2pMessage,
  SyncP2pMessage,
} from '@genially/p2p-lib';
import {
  BaseErrorCodes,
  BaseP2pMessageType,
  BaseServerDisconnectReason,
  BaseWebsocketClientToServerMessageType,
  BaseWebsocketServerToClientMessageType,
  ChunkManager,
  ChunkReceiver,
  ChunkSender,
  isBaseWebsocketBatchedMessages,
  isBaseWebsocketServerToClientMessage,
  isValidBaseP2pMessage,
  isValidSyncOrAcknowledgeP2pMessage,
} from '@genially/p2p-lib';
import { decodeError } from '@genially/ui/build/utils/decodeError';
import {
  addJitterToTime,
  calculate,
  MeasurePerformanceService,
  sleep,
} from '@genially/utils';

import { env } from '../config/env';
import type { Transport } from '../services/transport/Transport';
import { WebSocketTransport } from '../services/transport/WebSocketTransport';
import type { P2pLogger } from '../util/logger';
import { shouldUseIndexedServerUrls } from '../util/shouldUseIndexedServerUrls';
import { waitUntilGetServerIndex } from '../util/waitUntilGetServerIndex';
import { waitUntilServerIsAlive } from '../util/waitUntilServerIsAlive';
import { PeerSyncMessageTimeoutError } from './play/errors/PeerSyncMessageTimeoutError';
import { DebugInfo } from './play/utils/DebugInfo';

export type P2pConstructorParams = {
  roomId: string;

  /**
   * Used to calculate the server index which the client will connect
   */
  targetId: string;

  /**
   * Path to connect to the server
   */
  connectionPath: string;

  cookie?: string;
};

const InitialSyncMessageAverageResponseTime = 0;

export enum P2pClientEventType {
  ConnectedToServer = 'connected-to-server',
}

export type P2pClientEmittedEvents<
  T extends { [k: string]: Array<any> } = Record<string, any>,
> = {
  [P2pClientEventType.ConnectedToServer]: [void];
} & {
  [k in keyof T]: T[k];
};

const MaxAmountOfProcessingMessages = 100;

export abstract class P2pClient<
  /**
   * The type of messages that server will send directly to the client
   */
  WebsocketServerToClientMessage,
  /**
   * The type of messages that the client will send to the server
   */
  WebsocketClientToServerMessage,
  /**
   * The type of messages that the client will send to the peers
   */
  P2pMessage,
  /**
   * The type of error codes that the server can send to the client
   */
  ServerErrorCodes = any,
  /**
   * The type of reasons for which the server can disconnect the client
   */
  ServerDisconnectReason = any,
  /**
   * The type of metadata that the client has for each
   * peer connection
   */
  OnPeerConnectionMetadata = any,
  /**
   * The type of emitted events that the client can emit
   */
  EmittedEvents extends P2pClientEmittedEvents = P2pClientEmittedEvents,
> {
  protected _peerId: string | undefined;

  @observable
  public _pingTime: number | undefined;

  /**
   * Current connections with other peers
   * @protected
   */
  @observable
  protected readonly _connections = new Map<string, OnPeerConnectionMetadata>();

  protected readonly measurePerformanceService = new MeasurePerformanceService(
    'P2pClient',
  );

  private readonly serverConnectionPath: string;

  protected emitter = new EventEmitter<P2pClientEmittedEvents<any>>();

  protected readonly roomId: string;

  protected readonly targetId: string;

  private readonly cookie?: string;

  private lastServerIndex: number | undefined;

  @observable
  private serverConnectionPromise: Promise<void> | undefined;

  private serverConnectionAssertionInterval: number | undefined;

  protected disposed = false;

  @observable
  protected _connection: Transport | undefined;

  @observable
  protected _isConnected = false;

  /**
   * Map to store the callbacks of the sync messages
   * @private
   */
  private syncMessagesCallbacks = new Map<string, () => void>();

  /**
   * Average response time of the sync messages
   * @protected
   */
  @observable
  protected syncMessageAverageResponseTime = InitialSyncMessageAverageResponseTime;

  protected chunkManager = new ChunkManager();

  private processingMessagesBuffer: string[] = [];

  private amountOfProcessingMessages = 0;

  protected readonly _debugInfo = new DebugInfo(this);

  protected constructor({
    roomId,
    cookie,
    targetId,
    connectionPath,
  }: P2pConstructorParams) {
    this.serverConnectionPath = connectionPath;

    this.targetId = targetId;

    this.roomId = roomId;

    this.cookie = cookie;

    this.startServerConnectionAssertion();
  }

  private startServerConnectionAssertion() {
    this.stopServerConnectionAssertion();
    this.serverConnectionAssertionInterval = window.setInterval(() => {
      const needToReconnect =
        !this._connection ||
        (!this._connection.connecting && !this._connection.connected);

      if (needToReconnect && !this.isConnecting) {
        this.logger.warn(
          `Socket is in invalid state ${this._connection?.readyState}. Reconnecting...`,
        );

        this.connectToServer();
      }
    }, 2500);
  }

  private stopServerConnectionAssertion() {
    if (this.serverConnectionAssertionInterval) {
      clearInterval(this.serverConnectionAssertionInterval);
    }
  }

  get connection() {
    if (!this._connection) {
      throw new Error('Transport is not initialized');
    }

    return this._connection;
  }

  get debugInfo() {
    return this._debugInfo.data;
  }

  get isConnected() {
    return this._isConnected;
  }

  get isConnecting() {
    if (this.isConnected) {
      return false;
    }

    return (
      this.serverConnectionPromise !== undefined ||
      this._connection?.connecting !== undefined
    );
  }

  get isReadyToSendMessages() {
    return this._connection && this._connection.connected;
  }

  abstract get logger(): P2pLogger;

  async start() {
    return this.connectToServer();
  }

  get peerId(): string {
    if (!this._peerId) {
      throw new Error('Connection id is not initialized');
    }

    return this._peerId;
  }

  get pingTime(): number | undefined {
    return this._pingTime;
  }

  on<T extends keyof EmittedEvents>(
    event: T,
    listener: (p: EmittedEvents[T][number]) => void,
  ): this {
    this.emitter.on(event, listener);

    return this;
  }

  once<T extends keyof EmittedEvents>(
    event: T,
    listener: (p: EmittedEvents[T][number]) => void,
  ): this {
    this.emitter.once(event, listener);

    return this;
  }

  off<T extends keyof EmittedEvents>(
    event: T,
    listener: (p: EmittedEvents[T][number]) => void,
  ): this {
    this.emitter.off(event, listener);

    return this;
  }

  get connections() {
    return this._connections;
  }

  abstract buildTransportParams(): Record<string, string>;

  protected async connectToServer(options?: { retrying?: boolean }): Promise<void> {
    const retrying = options?.retrying === true;

    if (this.disposed) return Promise.resolve();
    if (this.serverConnectionPromise && !retrying) return this.serverConnectionPromise;

    // eslint-disable-next-line no-async-promise-executor
    this.serverConnectionPromise = new Promise<void>(async (resolve, reject) => {
      const serverIndex = await waitUntilGetServerIndex(this.targetId);

      await waitUntilServerIsAlive(this.targetId);

      if (this.lastServerIndex !== serverIndex) {
        this.logger.debug(
          `Server index changed from ${this.lastServerIndex} to ${serverIndex}`,
        );

        this.lastServerIndex = serverIndex;
      } else {
        const alreadyConnected = this._connection?.connected;

        if (alreadyConnected) {
          this.logger.debug('Already connected to server');
          resolve();

          return;
        }
      }

      const path = calculate(() => {
        const indexedServerPath = `${serverIndex}/${this.serverConnectionPath}`;

        if (shouldUseIndexedServerUrls) {
          this.logger.debug(`Connecting to server ${indexedServerPath}`);

          return indexedServerPath;
        }

        this.logger.debug(
          `Connecting to server ${this.serverConnectionPath} but in production will be: ${indexedServerPath}`,
        );

        return this.serverConnectionPath;
      });

      if (this._connection) {
        this.disconnectFromServer();
      }

      const newConnection = new WebSocketTransport({
        url: `${env.appUrls.p2pServer}/${path}`,
        roomId: this.roomId,
        targetId: this.targetId,
        cookie: this.cookie,
        otherParams: this.buildTransportParams(),
        logger: this.logger,
      });

      this._connection = newConnection;

      this.setConnectionListeners();

      const onConnect = () => {
        newConnection.emitter.off('open', onConnect);

        clearTimeout(timeout);

        this.startServerConnectionAssertion();

        resolve();
      };

      const timeout = setTimeout(() => {
        newConnection.emitter.off('open', onConnect);

        this.removeConnectionListeners();

        this.logger.warn('Connection to server timed out. Retrying...');

        this.connectToServer({ retrying: true }).then(resolve).catch(reject);
      }, addJitterToTime(10000));

      newConnection.emitter.on('open', onConnect);
    }).finally(() => {
      this.serverConnectionPromise = undefined;
    });

    return this.serverConnectionPromise;
  }

  protected disconnectFromServer() {
    this.stopServerConnectionAssertion();
    this.removeConnectionListeners();
    this._connection?.close();
  }

  @action
  protected onServerConnection() {
    this.logger.debug(`Connected to server`);

    this.emitter.emit(P2pClientEventType.ConnectedToServer);

    this._isConnected = true;
  }

  private setConnectionListeners() {
    if (!this._connection) {
      throw new Error('Transport is not initialized');
    }

    this._connection.emitter.on('open', this.handleSocketOpen.bind(this));
    this._connection.emitter.on('message', this.handleSocketMessage.bind(this));
    this._connection.emitter.on('close', this.handleSocketClose.bind(this));
    this._connection.emitter.on('error', this.handleSocketError.bind(this));
  }

  private removeConnectionListeners() {
    if (!this._connection) {
      return;
    }

    this._connection.emitter.off('open', this.handleSocketOpen.bind(this));
    this._connection.emitter.off('message', this.handleSocketMessage.bind(this));
    this._connection.emitter.off('close', this.handleSocketClose.bind(this));
    this._connection.emitter.off('error', this.handleSocketError.bind(this));
  }

  protected onPeerConnected(_: string, __: OnPeerConnectionMetadata) {
    // Implementation must handle it
  }

  protected onSetPeers(_: Record<string, OnPeerConnectionMetadata>) {
    // Implementation must handle it
  }

  private async handleSyncOrNAcknowledgeP2pMessage(
    peerId: string,
    message: SyncOrAcknowledgeP2pMessage<P2pMessage>,
  ): Promise<void> {
    if (message.type === BaseP2pMessageType.Acknowledge) {
      const messageId = message.payload.id;

      const callback = this.syncMessagesCallbacks.get(messageId);

      if (callback) {
        callback();
        this.syncMessagesCallbacks.delete(messageId);
      }
    } else {
      await this.onPeerMessage(peerId, message.payload.message);

      const acknowledgeMessage: AcknowledgeP2pMessage = {
        type: BaseP2pMessageType.Acknowledge,
        payload: {
          id: message.payload.id,
        },
      };

      this.sendToPeer(peerId, acknowledgeMessage);
    }
  }

  protected abstract onPeerMessage(peerId: string, message: P2pMessage): Promise<void>;

  protected onPeerDisconnected(_: string, __: OnPeerConnectionMetadata): void {
    // Implementation must handle it
  }

  abstract isValidP2pMessage(message: unknown): message is P2pMessage;

  private handlePeerConnected(peerId: string, metadata: OnPeerConnectionMetadata) {
    const connectionSet = this.setConnection(peerId, metadata);

    if (!connectionSet) {
      return;
    }

    this.onPeerConnected(peerId, metadata);

    this._debugInfo.onPeerConnectedOpen(peerId);
  }

  private async handlePeerMessage(peerId: string, data: unknown) {
    this._debugInfo.onPeerMessageReceived(peerId, data);

    if (isValidSyncOrAcknowledgeP2pMessage<P2pMessage>(data)) {
      await this.handleSyncOrNAcknowledgeP2pMessage(peerId, data);
      return;
    }

    if (isValidBaseP2pMessage(data)) {
      return;
    }

    if (this.isValidP2pMessage(data)) {
      await this.onPeerMessage(peerId, data as P2pMessage);
    }
  }

  private handlePeerDisconnected(peerId: string, metadata: OnPeerConnectionMetadata) {
    this.onPeerDisconnected(peerId, metadata);

    this._connections.delete(peerId);

    this._debugInfo.onPeerDisconnected(peerId);
  }

  protected async sendToPeers(
    peerIds: string[],
    data: P2pMessage | SyncP2pMessage<P2pMessage> | AcknowledgeP2pMessage,
  ) {
    if (!this.isReadyToSendMessages) {
      await sleep(addJitterToTime(5000));

      this.sendToPeers(peerIds, data);
      return;
    }

    peerIds.forEach(peerId => this._debugInfo.onPeerMessageSent(peerId, data));

    const proxyMessage: BaseWebsocketClientToServerPeerMessage<typeof data> = {
      type: BaseWebsocketClientToServerMessageType.PeerMessage,
      toPeerIds: peerIds,
      message: data,
    };

    this.send(JSON.stringify(proxyMessage));
  }

  protected sendToPeer(
    peerId: string,
    data: P2pMessage | SyncP2pMessage<P2pMessage> | AcknowledgeP2pMessage,
  ) {
    this.sendToPeers([peerId], data);
  }

  protected async sendToPeerSync(peerId: string, data: P2pMessage): Promise<void> {
    const id = nanoid();

    const message: SyncP2pMessage<P2pMessage> = {
      type: BaseP2pMessageType.Sync,
      payload: {
        id,
        message: data,
      },
    };

    this.sendToPeer(peerId, message);

    return new Promise<void>((resolve, reject) => {
      let rejected = false;

      const timeout = setTimeout(() => {
        rejected = true;
        reject(new PeerSyncMessageTimeoutError(`Sync message to ${peerId} timed out`));
      }, 10000);

      const startTime = Date.now();
      this.syncMessagesCallbacks.set(id, () => {
        const endTime = Date.now();

        this.setAverageSyncResponseTime(endTime - startTime);

        if (rejected) {
          return;
        }

        resolve();

        clearTimeout(timeout);

        this.syncMessagesCallbacks.delete(id);
      });
    });
  }

  protected sendBroadcast(data: P2pMessage) {
    this.sendToPeers(['*'], data);
  }

  protected async sendToAllPeersSync(data: P2pMessage) {
    const send = async (peerId: string) => {
      try {
        await this.sendToPeerSync(peerId, data);
      } catch (e) {
        if (e instanceof PeerSyncMessageTimeoutError) {
          await send(peerId);
        } else {
          throw e;
        }
      }
    };

    await Promise.all(Array.from(this._connections.keys()).map(send));
  }

  protected sendWebsocketMessage(data: WebsocketClientToServerMessage) {
    this.send(JSON.stringify(data));
  }

  send(data: string) {
    if (!this._connection) {
      throw new Error('Transport is not initialized. Cannot send data.');
    }

    const dataAsJson = JSON.parse(data);

    const isAnyChunkMessage =
      ChunkReceiver.isChunkMessage(dataAsJson) ||
      ChunkSender.isChunkAckMessage(dataAsJson);

    if (!isAnyChunkMessage && ChunkSender.needToBeChunked(data)) {
      this.chunkManager.sendChunkedMessage(data, c => this.send(JSON.stringify(c)));
      return;
    }

    this._connection.send(data);
  }

  private handleSocketMessage(message: MessageEvent): void {
    if (!message.data) {
      this.logger.warn('Received empty message from server');
      return;
    }

    if (this.amountOfProcessingMessages <= MaxAmountOfProcessingMessages) {
      this.handleSocketMessageData(message.data);
      return;
    }

    this.processingMessagesBuffer.push(message.data);
  }

  async handleSocketMessageData(data: string) {
    try {
      this.amountOfProcessingMessages += 1;

      const decodedMessage = JSON.parse(data);

      this._debugInfo.onMessageReceivedFromServer(decodedMessage);

      if (ChunkSender.isChunkAckMessage(decodedMessage)) {
        this.chunkManager.onChunkAckReceived(decodedMessage);
        return;
      }

      if (ChunkReceiver.isChunkMessage(decodedMessage)) {
        this.chunkManager.onChunkedMessageReceived(
          decodedMessage,
          c => this.send(JSON.stringify(c)),
          m => this.handleSocketMessageData(m),
        );
        return;
      }

      const handleMessage = async (message: any) => {
        if (this.isValidWebsocketMessage(message)) {
          this.onWebsocketMessage(message);
          return;
        }

        if (
          isBaseWebsocketServerToClientMessage<P2pMessage, OnPeerConnectionMetadata>(
            message,
          )
        ) {
          await this.handleBaseWebsocketMessage(message);
          return;
        }

        this.logger.warn('Unknown message', { message });
      };

      if (isBaseWebsocketBatchedMessages(decodedMessage)) {
        decodedMessage.messages.forEach(handleMessage);
      } else {
        await handleMessage(decodedMessage);
      }
    } catch (error) {
      this.logger.error('Error parsing websocket message', {
        error: decodeError(error),
      });
    } finally {
      this.amountOfProcessingMessages -= 1;

      const nextMessage = this.processingMessagesBuffer.shift();

      if (nextMessage) {
        this.handleSocketMessageData(nextMessage);
      }
    }
  }

  abstract isValidWebsocketMessage(
    message: unknown,
  ): message is WebsocketServerToClientMessage;

  private handleSocketOpen() {
    this.logger.debug('Socket open');

    this._connections.clear();

    this.onServerConnection();
  }

  private async handleSocketClose(reason?: BaseServerDisconnectReasonMessage) {
    this._isConnected = false;

    const retry = async (delay = 2500) => {
      await sleep(addJitterToTime(delay));

      this.connectToServer();
    };

    if (!reason) {
      retry(2500);

      return;
    }

    this.logger.debug(`Socket closed with reason: ${reason.type}`);

    if (this.isValidServerDisconnectReason(reason.type)) {
      this.onServerDisconnection(reason.type);

      return;
    }

    switch (reason.type) {
      case BaseServerDisconnectReason.Error:
        this.handleSocketError(reason.error);
        break;
      case BaseServerDisconnectReason.Unknown:
      case BaseServerDisconnectReason.Timeout:
      case BaseServerDisconnectReason.RoomClosed:
        retry(5000);
        break;
      default:
        this.logger.error('Unhandled server disconnection reason', { reason });
        retry();
    }
  }

  protected abstract isValidServerDisconnectReason(
    reason: unknown,
  ): reason is ServerDisconnectReason;

  protected abstract onServerDisconnection(reason: ServerDisconnectReason): void;

  protected abstract isValidWebsocketServerError(code: unknown): code is ServerErrorCodes;

  private async handleSocketError(message?: BaseWebsocketServerErrorMessage) {
    if (message !== undefined) {
      if (this.isValidWebsocketServerError(message.code)) {
        this.onServerError(message.code);
        return;
      }

      this.handleServerError(message);
      return;
    }

    const decodedError = decodeError(message);

    if (decodedError.message) {
      this.logger.error('Unhandled server error:', {
        error: decodedError,
      });
    }

    await sleep(addJitterToTime(2500));

    this.connectToServer();
  }

  private async handleBaseWebsocketMessage(
    message: BaseWebsocketServerToClientMessage<P2pMessage, OnPeerConnectionMetadata>,
  ) {
    switch (message.type) {
      case BaseWebsocketServerToClientMessageType.PeerId:
        this.handlePeerIdMessage(message.peerId);
        break;
      case BaseWebsocketServerToClientMessageType.Ping:
        this.sendPong(message.latency);
        break;
      case BaseWebsocketServerToClientMessageType.PeerMessage:
        const { message: proxiedMessage, peerId } = message;

        await this.handlePeerMessage(peerId, proxiedMessage);
        break;
      case BaseWebsocketServerToClientMessageType.PeerConnected:
        this.handlePeerConnected(message.peerId, message.metadata);
        break;
      case BaseWebsocketServerToClientMessageType.PeerDisconnected:
        this.handlePeerDisconnected(message.peerId, message.metadata);
        break;
      case BaseWebsocketServerToClientMessageType.SetPeers:
        this.handleSetPeers(message);
        break;
      case BaseWebsocketServerToClientMessageType.ServerError:
        this.handleServerError(message);
        break;
      default:
        this.logger.warn(`Unknown message`, { message });
    }
  }

  private handleServerError(message: BaseWebsocketServerErrorMessage) {
    const { handled } = this.onServerError(message.code);

    if (handled) return;

    switch (message.code) {
      case BaseErrorCodes.UnknownError:
      case BaseErrorCodes.ServerClosing:
        if (message.code === BaseErrorCodes.UnknownError) {
          this.logger.error('Unknown server error');
        }

        sleep(addJitterToTime(2500)).then(() => {
          this.connectToServer();
        });
        break;
      case BaseErrorCodes.Unauthorized:
      case BaseErrorCodes.InvalidAuthorizationRequest:
      case BaseErrorCodes.UnknownAuthorizationError:
      case BaseErrorCodes.NoRoomFound:
      case BaseErrorCodes.InvalidOrigin:
      case BaseErrorCodes.InvalidRoomId:
      case BaseErrorCodes.InvalidTargetId:
        this.logger.error(`Client disposed due to an error: ${message.code}`);

        this.dispose();
        break;
      default:
        // Implementation must handle it
        this.logger.error(`Unhandled by client server error: ${message.code}`);
        break;
    }
  }

  protected abstract onServerError(code: ServerErrorCodes | BaseErrorCodes): {
    handled: boolean;
  };

  private handlePeerIdMessage(peerId: string) {
    this._peerId = peerId;
    this.onPeerIdReceived();
  }

  abstract onPeerIdReceived(): void;

  private handleSetPeers(
    message: BaseWebsocketServerToClientPeersConnectedMessage<OnPeerConnectionMetadata>,
  ) {
    this._connections.clear();

    const connections: Record<string, OnPeerConnectionMetadata> = {};

    message.peerIds.forEach((peerId, index) => {
      const metadata = message.metadata[index];

      const connectionSet = this.setConnection(peerId, metadata);

      if (!connectionSet) {
        return;
      }

      connections[peerId] = metadata;

      this._debugInfo.onPeerConnectedOpen(peerId);
    });

    this.onSetPeers(connections);
  }

  protected abstract onWebsocketMessage(_: WebsocketServerToClientMessage): void;

  protected setConnection(peerId: string, metadata: OnPeerConnectionMetadata): boolean {
    this._connections.set(peerId, metadata);

    return true;
  }

  @action
  private setAverageSyncResponseTime(time: number) {
    if (this.syncMessageAverageResponseTime === InitialSyncMessageAverageResponseTime) {
      this.syncMessageAverageResponseTime = time;
    } else {
      this.syncMessageAverageResponseTime =
        (this.syncMessageAverageResponseTime + time) / 2;
    }
  }

  public getAverageSyncResponseTime() {
    return this.syncMessageAverageResponseTime;
  }

  @action
  private sendPong(latency: number | undefined) {
    const message: BaseWebsocketClientToServerPongMessage = {
      type: BaseWebsocketClientToServerMessageType.Pong,
      timestamp: Date.now(),
    };

    if (latency !== undefined) {
      this._pingTime = latency;
    }

    this.send(JSON.stringify(message));
  }

  dispose() {
    this.disposed = true;
    this.measurePerformanceService.dispose();

    this.disconnectFromServer();

    clearInterval(this.serverConnectionAssertionInterval);

    this.emitter = new EventEmitter<P2pClientEmittedEvents<any>>();
  }
}
